import React, { useState, useMemo, useEffect, useRef } from 'react';
import { FaSearch } from 'react-icons/fa';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import { Sidebar } from 'primereact/sidebar';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useDispatch } from 'react-redux';
import { fetchEntityRequest, editEntity, destroyEntity, fetchFlatCollectionEntities, regenerateApiDoc, deprecateEntity } from 'redux-store/currentUserActions';
import SwaggerDoc from 'components/Apis/SwaggerDoc';
import RequestDetails from './RequestDetails';
import ResponseDetails from './ResponseDetails';
import { ExternalLink, Edit, GalleryVerticalEndIcon, RefreshCw, Trash, Archive, FileText, EllipsisVerticalIcon, X } from 'lucide-react';
import CurlDetails from './CurlDetails';
import { Tooltip, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, Menu, MenuItem, FormControl, InputLabel, Select } from '@mui/material';
import { Copy } from 'lucide-react';
import { useSnackbar } from 'contexts/CustomSnackbarContext';
import { styled } from '@mui/material/styles';
import ApiFlowGraph from './ApiFlowGraph';
const getRandomColor = (tag) => {
    const colors = [
        '#6366F1', // Darker Indigo
        '#8B5CF6', // Darker Violet
        '#DB2777', // Darker Pink
        '#E11D48', // Darker Rose
        '#059669', // Darker Emerald
        '#3B82F6', // Darker Blue
        '#65A30D', // Darker Lime
        '#0891B2', // Darker Cyan
        '#9333EA', // Darker Purple
        '#DC2626', // Darker Red
        '#0D9488', // Darker Teal
        '#EA580C', // Darker Orange
        '#16A34A', // Darker Green
        '#0284C7'  // Darker Sky
    ];
    const index = tag.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[index % colors.length];
};

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`api-tabpanel-${index}`}
            aria-labelledby={`api-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ py: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `api-tab-${index}`,
        'aria-controls': `api-tabpanel-${index}`,
    };
}

const ApiEndpointTable = ({
    getMethodBadgeStyle,
    collectionId,
    setApiEndpointsCount = () => { },
    addEntityToChatContext = () => { },
    isAiAgent = false,
    selectedChatEntityIds = null
}) => {
    const dispatch = useDispatch();
    const { openSnackbar } = useSnackbar();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedApi, setSelectedApi] = useState(null);
    const [sidebarVisible, setSidebarVisible] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [requestData, setRequestData] = useState(null);
    const [apiEndpoints, setApiEndpoints] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedEntityIds, setSelectedEntityIds] = useState([]);
    const hasInitialSelection = useRef(false);
    // Sidebar
    const [isRefreshingAPI, setIsRefreshingAPI] = useState(false);
    const [editedDescription, setEditedDescription] = useState('');
    const [editedTags, setEditedTags] = useState([]);
    const [newTag, setNewTag] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const [editedTitle, setEditedTitle] = useState('');
    // Dialogs
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isDeprecateDialogOpen, setIsDeprecateDialogOpen] = useState(false);
    const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
    const [isEditApiModalOpen, setIsEditApiModalOpen] = useState(false);
    const [apiDependencies, setApiDependencies] = useState([]);
    const [selectionFilter, setSelectionFilter] = useState('all'); // 'all', 'selected', 'not_selected'

    const fetchEntityRequestData = async (entityId) => {
        try {
            const response = await dispatch(fetchEntityRequest(entityId)).unwrap();
            if (response) {
                setRequestData(response);
                setApiDependencies(response.api_dependencies || []);
            }
        } catch (error) {
            console.error('Failed to fetch entity request data:', error);
        }
    };

    const fetchApiEndpoints = async (collectionId) => {
        setIsLoading(true);
        if (!collectionId) {
            setApiEndpoints([]);
            setApiEndpointsCount(0);
            return;
        }
        try {
            const res = await dispatch(fetchFlatCollectionEntities(collectionId)).unwrap();
            setApiEndpoints(res.entities);
            setApiEndpointsCount(res.entities.length);
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching API endpoints:', error);
            setApiEndpoints([]);
            setApiEndpointsCount(0);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchApiEndpoints(collectionId);
    }, [collectionId]);

    const handleApiClick = async (api) => {
        setSelectedApi(api);
        await fetchEntityRequestData(api.id);
        setSidebarVisible(true);
    };

    const refreshOpenedApi = async () => {
        setIsRefreshingAPI(true);
        if (selectedApi) {
            await fetchEntityRequestData(selectedApi.id);
            setTimeout(() => {
                setIsRefreshingAPI(false);
            }, 600)
        }
    }

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleCopy = () => {
        if (requestData?.swagger_doc) {
            const jsonString = JSON.stringify(requestData.swagger_doc, null, 2);
            navigator.clipboard.writeText(jsonString);
        }
    };

    const handleAddTag = () => {
        if (newTag.trim() && !editedTags.includes(newTag.trim())) {
            setEditedTags([...editedTags, newTag.trim()]);
            setNewTag('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setEditedTags(editedTags.filter(tag => tag !== tagToRemove));
    };

    const processedRows = useMemo(() => {
        let filtered = apiEndpoints.filter(endpoint => {
            if (!searchQuery) return true;
            const searchLower = searchQuery.toLowerCase();
            return (
                endpoint.name?.toLowerCase().includes(searchLower) ||
                endpoint.request_type?.toLowerCase().includes(searchLower) ||
                endpoint.url?.toLowerCase().includes(searchLower) ||
                endpoint.folder_name?.toLowerCase().includes(searchLower) ||
                endpoint.api_endpoint?.toLowerCase().includes(searchLower) ||
                endpoint.tags?.some(tag => tag.toLowerCase().includes(searchLower)) ||
                endpoint.description?.toLowerCase().includes(searchLower) ||
                endpoint.no_of_test_scenario?.toString().includes(searchLower)
            );
        });

        if (selectionFilter === 'selected') {
            filtered = filtered.filter(row => selectedEntityIds.includes(row.id));
        } else if (selectionFilter === 'not_selected') {
            filtered = filtered.filter(row => !selectedEntityIds.includes(row.id));
        }
        return filtered;
    }, [apiEndpoints, searchQuery, selectionFilter, selectedEntityIds]);

    const openApiPathOnClick = (apiPath) => {
        if (apiPath) {
            window.open(apiPath, "_blank");
        }
    }

    const handleMenuClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (event) => {
        event?.stopPropagation();
        setAnchorEl(null);
        if (event?.currentTarget) {
            event.currentTarget.blur();
        }
    };

    const handleDeleteClick = (event) => {
        event.stopPropagation();
        handleMenuClose();
        setIsDeleteDialogOpen(true);
    };

    const handleDeprecateClick = (event) => {
        event.stopPropagation();
        handleMenuClose();
        setIsDeprecateDialogOpen(true);
    };

    const handleGenerateClick = (event) => {
        event.stopPropagation();
        handleMenuClose();
        setIsGenerateDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            const response = await dispatch(destroyEntity({ id: selectedApi.id })).unwrap();
            if (response) {
                openSnackbar({ message: "API deleted successfully", severity: 'success' });
                setSidebarVisible(false);
                await fetchApiEndpoints(collectionId);
            }
        } catch (error) {
            console.error('Failed to delete API:', error);
            openSnackbar({ message: "Failed to delete API", severity: 'error' });
        }
        setIsDeleteDialogOpen(false);
    };

    const handleDeprecateConfirm = async () => {
        if (!selectedApi) return;
        try {
            const response = await dispatch(deprecateEntity({
                entityId: selectedApi.id
            })).unwrap();
            if (response) {
                openSnackbar({ message: "API deprecated successfully", severity: 'success' });
                await fetchEntityRequestData(selectedApi.id);
            }
        } catch (error) {
            console.error('Failed to deprecate API:', error);
            openSnackbar({ message: "Failed to deprecate API", severity: 'error' });
        }
        setIsDeprecateDialogOpen(false);
    };

    const handleGenerateConfirm = async () => {
        setIsGenerateDialogOpen(false);
        openSnackbar({ message: "API generation started", severity: 'info' });
        try {
            const response = await dispatch(regenerateApiDoc({ entityId: selectedApi.id })).unwrap();
            if (response) {
                openSnackbar({ message: "API generation completed", severity: 'success' });
                await fetchEntityRequestData(selectedApi.id);
            }
        } catch (error) {
            console.error('Failed to generate API:', error);
            openSnackbar({ message: "Failed to generate API", severity: 'error' });
        }
    };

    const handleEditApiClick = (event) => {
        event.stopPropagation();
        handleMenuClose();
        setEditedTitle(requestData?.name || '');
        setEditedDescription(requestData?.description || '');
        setEditedTags(requestData?.tags || []);
        setIsEditApiModalOpen(true);
    };

    const handleUpdateApi = async () => {
        try {
            const response = await dispatch(editEntity({
                entityId: selectedApi.id,
                updateData: {
                    name: editedTitle,
                    description: editedDescription,
                    tags: editedTags
                }
            })).unwrap();

            if (response) {
                await fetchEntityRequestData(selectedApi.id);
                await fetchApiEndpoints(collectionId);
                openSnackbar({ message: "API updated successfully", severity: 'success' });
                setIsEditApiModalOpen(false);
            }
        } catch (error) {
            console.error('Failed to update API:', error);
            openSnackbar({ message: "Failed to update API", severity: 'error' });
        }
    };

    const handleUpdateApiDependencies = async (apiDependencies) => {
        try {
            const response = await dispatch(editEntity({
                entityId: selectedApi.id,
                updateData: { api_dependencies: apiDependencies }
            })).unwrap();

            if (response) {
                await fetchEntityRequestData(selectedApi.id);
                await fetchApiEndpoints(collectionId);
                openSnackbar({ message: "API dependencies updated successfully", severity: 'success' });
            }
        } catch (error) {
            console.error('Failed to update API:', error);
            openSnackbar({ message: "Failed to update API", severity: 'error' });
        }
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            // Get all IDs from processedRows (filtered view)
            const filteredIds = processedRows.map(row => row.id);
            // Get IDs that are not in the current filtered view
            const otherSelectedIds = selectedEntityIds.filter(id => !filteredIds.includes(id));
            // Combine both sets of IDs
            setSelectedEntityIds([...otherSelectedIds, ...filteredIds]);
        } else {
            // When unchecking, remove all IDs from the current filtered view
            const filteredIds = new Set(processedRows.map(row => row.id));
            setSelectedEntityIds(selectedEntityIds.filter(id => !filteredIds.has(id)));
        }
    };

    const handleSelectItem = (id) => {
        setSelectedEntityIds(prev => {
            if (prev.includes(id)) {
                return prev.filter(itemId => itemId !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    useEffect(() => {
        if (isAiAgent && processedRows.length > 0 && !hasInitialSelection.current) {
            if (selectedChatEntityIds && selectedChatEntityIds.length > 0) {
                setSelectedEntityIds(selectedChatEntityIds);
            } else {
                const allIds = processedRows.map(row => row.id);
                setSelectedEntityIds(allIds);
            }
            hasInitialSelection.current = true;
        }
    }, [processedRows]);

    const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
        padding: theme.spacing(0.5, 0.75),
        backgroundColor: "var(--background-primary)",
        fontWeight: 600,
        fontSize: '0.875rem',
        color: "var(--text-primary)",
        borderBottom: `1px solid var(--borderColor)`,
        '&.checkbox-cell': {
            width: '32px',
            padding: theme.spacing(0.5),
        },
        '&.date-cell': {
            width: '140px', // Fixed width for date column
            padding: theme.spacing(1, 1.5),
        },
        '&.action-cell': {
            width: '48px',
            padding: theme.spacing(0.5),
        }
    }));

    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        padding: theme.spacing(1, 1.5), // Consistent base padding
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&.checkbox-cell': {
            width: '32px',
            padding: theme.spacing(0.5),
        },
        '&.date-cell': {
            width: '140px', // Fixed width for date column
            padding: theme.spacing(1, 1.5),
            whiteSpace: 'nowrap',
        },
        '&.action-cell': {
            width: '48px',
            padding: theme.spacing(0.5),
        }
    }));

    const buttonStyle = {
        textTransform: 'none',
        borderRadius: '6px',
        borderColor: 'var(--borderColor)',
        color: 'var(--text-primary)',
        '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'var(--hover-bg)'
        }
    };

    const renderTableContent = () => {
        if (isLoading) {
            return (
                <TableRow>
                    <StyledTableCell colSpan={7} align="center" sx={{ height: '200px' }}>
                        <CircularProgress />
                    </StyledTableCell>
                </TableRow>
            );
        }

        if (processedRows.length === 0) {
            return (
                <TableRow>
                    <StyledTableCell colSpan={7} align="center" sx={{ height: '200px' }}>
                        <div className="text-[var(--text-primary)]">
                            {searchQuery ? "No results found for your search." : "No API endpoints found for this collection."}
                        </div>
                    </StyledTableCell>
                </TableRow>
            );
        }

        return processedRows.map((row) => (
            <TableRow key={row.id} hover>
                {isAiAgent && (
                    <StyledTableCell className="checkbox-cell" align='center'>
                        <input
                            type="checkbox"
                            checked={selectedEntityIds.includes(row.id)}
                            onChange={() => handleSelectItem(row.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 rounded border-gray-300"
                            style={{
                                accentColor: 'var(--primary-color)'
                            }}
                        />
                    </StyledTableCell>
                )}
                <StyledTableCell sx={{ width: "90px" }}>
                    <span className={getMethodBadgeStyle(row.request_type)}>
                        {row.request_type}
                    </span>
                </StyledTableCell>
                <StyledTableCell
                    sx={{ width: "20%", cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    onClick={() => handleApiClick(row)}
                >
                    {row.folder_name ? row.folder_name + " /" : ""} {row.name}
                </StyledTableCell>
                <StyledTableCell sx={{ wordBreak: "break-word", width: "30%" }}>
                    {row.api_endpoint || ""}
                </StyledTableCell>
                <StyledTableCell>
                    {row.no_of_test_scenario}
                </StyledTableCell>
                <StyledTableCell>
                    <div className="flex flex-wrap gap-1">
                        {row.tags?.map((tag, index) => (
                            <Chip
                                key={index}
                                label={tag}
                                size="small"
                                sx={{
                                    backgroundColor: getRandomColor(tag),
                                    color: "white",
                                    fontWeight: 400,
                                    '& .MuiChip-label': {
                                        padding: '0 8px',
                                    },
                                }}
                            />
                        ))}
                    </div>
                </StyledTableCell>
            </TableRow>
        ));
    };

    return (
        <>
            <div className="px-2 py-3 border-b flex items-center justify-between bg-[var(--background-primary)]">
                <div className="flex items-center gap-2">
                    <h4 className='fs18 mb-0'>
                        API Endpoints <span className='fs14 text-[var(--text-secondary)]'>(Total: {apiEndpoints?.length})</span>
                    </h4>
                    {(searchQuery || selectionFilter !== "all") && (
                        <>(Results: {processedRows.length} APIs)</>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {isAiAgent && (
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => {
                                addEntityToChatContext(selectedEntityIds);
                            }}
                            disabled={selectedEntityIds.length === 0}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '6px',
                                backgroundColor: 'var(--primary-color)'
                            }}
                        >
                            Add To Chat ({selectedEntityIds.length})
                        </Button>
                    )}
                    {isAiAgent && (
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel id="selection-filter-label">Filter</InputLabel>
                            <Select
                                labelId="selection-filter-label"
                                id="selection-filter"
                                value={selectionFilter}
                                label="Filter"
                                variant="outlined"
                                onChange={(e) => setSelectionFilter(e.target.value)}
                                sx={{
                                    borderRadius: '6px',
                                    '& .MuiSelect-select': {
                                        padding: '4.5px 8px',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--borderColor)',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--borderColor)',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--borderColor)',
                                    },
                                }}
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="selected">Selected</MenuItem>
                                <MenuItem value="not_selected">Not Selected</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                    <div className="flex items-center w-64 px-3 py-1 rounded-md" style={{ border: '1px solid var(--borderColor)' }}>
                        <FaSearch className="text-gray-400 text-sm mr-2" />
                        <InputBase
                            placeholder="Search APIs..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="flex-1"
                            size="small"
                            sx={{ fontSize: '0.875rem', '& input': { padding: '2px 0' } }}
                        />
                    </div>
                </div>
            </div>
            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    maxHeight: 'calc(100vh - 275px)',
                }}
            >
                <Table stickyHeader aria-label="API table" sx={{
                    borderCollapse: "collapse"
                }}>
                    <TableHead>
                        <TableRow>
                            {isAiAgent && (
                                <StyledTableHeaderCell className="checkbox-cell min-w-12" align='center'>
                                    <input
                                        type="checkbox"
                                        checked={processedRows.length > 0 && processedRows.every(row => selectedEntityIds.includes(row.id))}
                                        onChange={handleSelectAll}
                                        className="w-4 h-4 rounded border-gray-300"
                                        style={{
                                            accentColor: 'var(--primary-color)'
                                        }}
                                    />
                                </StyledTableHeaderCell>
                            )}
                            <StyledTableHeaderCell>
                                Method
                            </StyledTableHeaderCell>
                            <StyledTableHeaderCell>
                                Name
                            </StyledTableHeaderCell>
                            <StyledTableHeaderCell>
                                URL
                            </StyledTableHeaderCell>
                            <StyledTableHeaderCell>
                                Active Test Scenarios
                            </StyledTableHeaderCell>
                            <StyledTableHeaderCell>
                                Tags
                            </StyledTableHeaderCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {renderTableContent()}
                    </TableBody>
                </Table>
            </TableContainer>

            <Sidebar
                visible={sidebarVisible}
                onHide={() => setSidebarVisible(false)}
                position="right"
                className="w-[60%] bgPrimary border !border-[var(--borderColor)] text-[var(--text-primary)]"
                modal={true}
                showCloseIcon={false}
                header={
                    <div className="flex justify-between items-center px-1 py-2 w-full">
                        <div className='flex items-center gap-2'>
                            <span className="text-xl font-medium">
                                API Details
                            </span>
                            {requestData?.deprecated && (
                                <span className="px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-800">DEPRECATED API</span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <Tooltip title="Open API in Collection">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => openApiPathOnClick(selectedApi.api_path)}
                                    startIcon={<ExternalLink size={16} />}
                                    sx={buttonStyle}
                                >
                                    Run API
                                </Button>
                            </Tooltip>
                            <Tooltip title="Edit API">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleEditApiClick}
                                    startIcon={<Edit size={16} />}
                                    sx={buttonStyle}
                                >
                                    Edit API
                                </Button>
                            </Tooltip>
                            <Tooltip title="Refresh API">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={refreshOpenedApi}
                                    sx={{
                                        ...buttonStyle,
                                        px: '6px',
                                        minWidth: '32px',
                                    }}
                                >
                                    <RefreshCw size={18} className={`${isRefreshingAPI ? 'animate-spin' : ''}`} />
                                </Button>
                            </Tooltip>
                            <Tooltip title="More Actions">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleMenuClick}
                                    sx={{
                                        ...buttonStyle,
                                        px: '6px',
                                        minWidth: '32px',
                                    }}
                                >
                                    <EllipsisVerticalIcon size={18} />
                                </Button>
                            </Tooltip>
                            <Tooltip title="Close">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setSidebarVisible(false)}
                                    sx={{
                                        ...buttonStyle,
                                        px: '6px',
                                        minWidth: '32px',
                                    }}
                                >
                                    <X size={18} />
                                </Button>
                            </Tooltip>
                        </div>
                    </div>
                }
            >
                {selectedApi && (
                    <div className="p-1">
                        <div className="flex items-center gap-2 mb-4">
                            <span className={`!text-base ${getMethodBadgeStyle(requestData?.request_type)}`}>
                                {requestData?.request_type}
                            </span>
                            <span className="!text-base text-[var(--text-primary)] break-words">
                                {requestData?.name}
                            </span>
                            <div className="ml-auto">
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleMenuClose}
                                    onClick={(e) => e.stopPropagation()}
                                    autoFocus={false}
                                    disableAutoFocusItem={true}
                                >
                                    <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                                        <Trash size={16} /> <span className='ml-1'>Delete</span>
                                    </MenuItem>
                                    {!requestData?.deprecated && (
                                        <MenuItem onClick={handleDeprecateClick} sx={{ color: 'warning.main' }}>
                                            <Archive size={16} /> <span className='ml-1'>Deprecate</span>
                                        </MenuItem>
                                    )}
                                    <MenuItem onClick={handleGenerateClick} sx={{ color: 'info.main' }}>
                                        <FileText size={16} /><span className='ml-1'>Generate Again</span>
                                    </MenuItem>
                                </Menu>
                            </div>
                        </div>
                        <div className="mb-4  text-[var(--text-primary)] break-words">
                            <div style={{ whiteSpace: 'pre-wrap' }}>
                                {requestData?.description || "No description available"}
                            </div>
                        </div>
                        <div className="mb-4  text-[var(--text-primary)] break-words">
                            <div className="flex items-center gap-1">
                                <Tooltip placement='top' title="Collection Name" arrow>
                                    <span>
                                        <GalleryVerticalEndIcon size={14} />
                                    </span>
                                </Tooltip>
                                <span className=" text-[var(--text-primary)]">
                                    {requestData?.collection_name}
                                </span>
                            </div>
                        </div>
                        <div className="mb-4">
                            <div className="flex flex-wrap gap-1">
                                {requestData?.tags?.map((tag, index) => (
                                    <Chip
                                        key={index}
                                        label={tag}
                                        size="small"
                                        sx={{
                                            backgroundColor: getRandomColor(tag),
                                            color: 'white',
                                            fontWeight: 400,
                                            '& .MuiChip-label': {
                                                padding: '0 8px',
                                            },
                                        }}
                                    />
                                ))}
                            </div>
                        </div>

                        <Box sx={{ width: '100%', bgcolor: 'background.paper' }}>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <Tabs
                                    value={tabValue}
                                    onChange={handleTabChange}
                                    aria-label="api details tabs"
                                    sx={{
                                        '& .MuiTabs-indicator': {
                                            backgroundColor: 'var(--primary-color)',
                                        },
                                        '& .Mui-selected': {
                                            color: 'var(--text-primary) !important',
                                        },
                                    }}
                                >
                                    <Tab label="Schema" {...a11yProps(0)} />
                                    <Tab label="Request" {...a11yProps(1)} />
                                    <Tab label="Response" {...a11yProps(2)} />
                                    <Tab label="cURL" {...a11yProps(3)} />
                                    <Tab label="Dependency Graph" {...a11yProps(4)} />
                                </Tabs>
                            </Box>
                            <TabPanel value={tabValue} index={0}>
                                {selectedApi && (
                                    <div className="space-y-4">
                                        <div className="bgPrimary rounded-lg border !border-[var(--borderColor)] shadow-sm hover:shadow-md transition-shadow duration-200">
                                            <div className="px-4 py-2 flex justify-between items-center" style={{ borderBottom: '2px solid var(--borderColor)' }}>
                                                <span className="!text-xl text-[var(--text-secondary)]">Schema</span>
                                                <Tooltip title="Copy To Clipboard" placement='bottom'>
                                                    <button
                                                        onClick={handleCopy}
                                                        className="group relative bgPrimary rounded-full transition-all duration-200 ease-in-out border-none"
                                                    >
                                                        <Copy size={18} className="border-none text-[var(--text-primary)] group-hover:text-gray-700 transition-colors duration-200" />
                                                    </button>
                                                </Tooltip>
                                            </div>
                                            <div className='overflow-y-auto h-[calc(100vh-310px)]'>
                                                {requestData?.swagger_doc ? (
                                                    <SwaggerDoc swaggerDoc={requestData.swagger_doc} />
                                                ) : (
                                                    <p className="text-[var(--text-primary)]">No schema information available</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </TabPanel>
                            <TabPanel value={tabValue} index={1}>
                                {selectedApi && (
                                    <RequestDetails requestData={requestData} />
                                )}
                            </TabPanel>
                            <TabPanel value={tabValue} index={2}>
                                {selectedApi && (
                                    <ResponseDetails requestData={requestData} />
                                )}
                            </TabPanel>
                            <TabPanel value={tabValue} index={3}>
                                {selectedApi && (
                                    <CurlDetails curlData={requestData} />
                                )}
                            </TabPanel>
                            <TabPanel value={tabValue} index={4}>
                                {selectedApi && (
                                    <ApiFlowGraph dependencyGraph={apiDependencies} endpoints={apiEndpoints} handleUpdateApiDependencies={handleUpdateApiDependencies} />
                                )}
                            </TabPanel>
                        </Box>
                    </div>
                )}
            </Sidebar>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={isDeleteDialogOpen}
                onClose={(e) => {
                    e.stopPropagation();
                    setIsDeleteDialogOpen(false);
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <DialogTitle>Delete API</DialogTitle>
                <DialogContent onClick={(e) => e.stopPropagation()}>
                    <p>Are you sure you want to delete this API? This action cannot be undone.</p>
                </DialogContent>
                <DialogActions onClick={(e) => e.stopPropagation()}>
                    <Button
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsDeleteDialogOpen(false);
                        }}
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteConfirm();
                        }}
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Deprecate Confirmation Dialog */}
            <Dialog
                open={isDeprecateDialogOpen}
                onClose={(e) => {
                    e.stopPropagation();
                    setIsDeprecateDialogOpen(false);
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <DialogTitle>Deprecate API</DialogTitle>
                <DialogContent onClick={(e) => e.stopPropagation()}>
                    <p>Are you sure you want to deprecate this API? This will mark it as deprecated in the system.</p>
                </DialogContent>
                <DialogActions onClick={(e) => e.stopPropagation()}>
                    <Button
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsDeprecateDialogOpen(false);
                        }}
                        variant="outlined"
                        color='error'
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeprecateConfirm();
                        }}
                        color="primary"
                        variant="contained"
                    >
                        Deprecate
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Generate Again Confirmation Dialog */}
            <Dialog
                open={isGenerateDialogOpen}
                onClose={(e) => {
                    e.stopPropagation();
                    setIsGenerateDialogOpen(false);
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <DialogTitle>Generate API Again</DialogTitle>
                <DialogContent onClick={(e) => e.stopPropagation()}>
                    <p>Are you sure you want to regenerate this API? This will create a new version of the API.</p>
                </DialogContent>
                <DialogActions onClick={(e) => e.stopPropagation()}>
                    <Button
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsGenerateDialogOpen(false);
                        }}
                        variant="outlined"
                        color='error'
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleGenerateConfirm();
                        }}
                        color="primary"
                        variant="contained"
                    >
                        Generate
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit API Modal */}
            <Dialog
                open={isEditApiModalOpen}
                onClose={(e) => {
                    e.stopPropagation();
                    setIsEditApiModalOpen(false);
                }}
                maxWidth="sm"
                fullWidth
                onClick={(e) => e.stopPropagation()}
            >
                <DialogTitle>Edit API Details</DialogTitle>
                <DialogContent onClick={(e) => e.stopPropagation()}>
                    <div className="space-y-4 mt-2">
                        <TextField
                            autoFocus
                            margin="dense"
                            label="API Title"
                            fullWidth
                            value={editedTitle}
                            onChange={(e) => setEditedTitle(e.target.value)}
                            variant="outlined"
                        />
                        <TextField
                            margin="dense"
                            label="Description"
                            fullWidth
                            multiline
                            rows={4}
                            value={editedDescription}
                            onChange={(e) => setEditedDescription(e.target.value)}
                            variant="outlined"
                        />
                        <div className="space-y-2">
                            <div className="flex gap-2">
                                <TextField
                                    size="small"
                                    label="Add new tag"
                                    value={newTag}
                                    onChange={(e) => setNewTag(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddTag();
                                        }
                                    }}
                                    fullWidth
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleAddTag}
                                    disabled={!newTag.trim()}
                                >
                                    Add
                                </Button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {editedTags.map((tag, index) => (
                                    <Chip
                                        key={index}
                                        label={tag}
                                        onDelete={() => handleRemoveTag(tag)}
                                        sx={{
                                            backgroundColor: getRandomColor(tag),
                                            color: "var(--text-primary)",
                                            fontWeight: 500,
                                            '& .MuiChip-deleteIcon': {
                                                color: "var(--text-primary)",
                                                '&:hover': {
                                                    color: "var(--text-secondary)",
                                                },
                                            },
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions onClick={(e) => e.stopPropagation()}>
                    <Button
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsEditApiModalOpen(false);
                        }}
                        variant="outlined"
                        color='error'
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateApi();
                        }}
                        color="primary"
                        variant="contained"
                    >
                        Update
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ApiEndpointTable;
